import React, { useState } from 'react';
import { ColorResult, ChromePicker } from 'react-color';
import { FieldRenderProps } from 'react-final-form';

// material-ui
import { Box, Grid, Typography, Popover } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

// components
import TextField from 'components/TextField';

interface ColorPickerProps extends FieldRenderProps<string, HTMLElement> {
  label: string;
  placeholder?: string;
  onScrollToBottom?: () => void;
}

const ColorPicker = ({
  input: { value, onChange },
  meta,
  label,
  placeholder,
  onScrollToBottom,
}: ColorPickerProps) => {
  const classes = useStyles();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleColorChange = (colorResult: ColorResult) => {
    onChange(colorResult.hex);
  };

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    onChange(newValue);
  };

  const toggleColorPicker = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => setAnchorEl(null);

  // Use color value or placeholder for display, but allow empty preview
  const previewColor = value || placeholder || '#f0f0f0';

  return (
    <Box>
      <Grid className={classes.labelContainer}>
        <Typography variant='body2' color='textSecondary'>
          {label}
        </Typography>
      </Grid>

      {/* Color preview and text input container */}
      <Grid container spacing={1} alignItems='center'>
        {/* Color preview box - clickable to open picker */}
        <Grid item>
          <Box
            className={classes.colorPreview}
            style={{ backgroundColor: previewColor, opacity: value ? 1 : 0.5 }}
            onClick={toggleColorPicker}
          />
        </Grid>

        {/* Text input field */}
        <Grid item xs={8}>
          <TextField
            value={value}
            onClick={toggleColorPicker}
            onChange={handleTextChange}
            placeholder={placeholder}
            margin='none'
            inputProps={{
              readOnly: true,
              className: classes.textInputProps,
            }}
          />
        </Grid>
      </Grid>

      {/* Color picker popover */}
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
      >
        <ChromePicker
          color={previewColor}
          disableAlpha
          onChange={handleColorChange}
        />
      </Popover>

      {/* Error message */}
      {meta.error && (
        <Typography variant='caption' color='error'>
          {meta.error}
        </Typography>
      )}
    </Box>
  );
};

const useStyles = makeStyles(theme => ({
  labelContainer: { marginBottom: theme.spacing(1) },
  colorPreview: {
    width: 28,
    height: 28,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    cursor: 'pointer',
  },
  textInputProps: { padding: theme.spacing(0.75, 1) },
}));

export default ColorPicker;
